import { Image } from "@/components/Image";
import { MediaCarousel } from "@/components/MediaCarousel";
import { useState } from "react";
import { ExternalLink, Film, Instagram, Play } from "lucide-react";
import { cn } from "@/utils/helpers";
import type { IdeaReference } from "@/types/contentCalendar.type";

/**
 * The reference well.
 *
 * Fixed height, full-bleed media (`object-contain`) so every card in a pitch row
 * reserves the same vertical band — the whole frame stays visible inside the
 * well instead of being cropped. When a sibling has a reference and this card
 * does not, pass `reserveSpace` for a blank spacer (no empty bordered box) so
 * the row still lines up.
 *
 * A reference only exists when one of the idea's sources is a trend: the server
 * picks one example video of that trend and resolves a fresh playable URL on
 * every read. `videoUrl` can be null (the example was purged) while `pageUrl`
 * still points at the live post, so the well degrades to a thumbnail-and-link
 * rather than disappearing.
 */

const WELL_HEIGHT = "h-[350px]";

/** lucide-react ships no TikTok brand icon, so we inline the glyph. */
function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  );
}

function PlatformOpenIcon({ platform }: { platform: string }) {
  const key = platform.trim().toLowerCase();
  if (key === "instagram") {
    return <Instagram className="size-3.5" strokeWidth={2} />;
  }
  if (key === "tiktok") {
    return <TikTokIcon className="size-3.5" />;
  }
  return <ExternalLink className="size-3.5" strokeWidth={2} />;
}

export function ReferenceWell({
  reference,
  reserveSpace = false,
  className,
}: {
  reference: IdeaReference | null;
  /** Keep the fixed-height band even with no reference (sibling alignment). */
  reserveSpace?: boolean;
  className?: string;
}) {
  const [playing, setPlaying] = useState(false);
  if (!reference && !reserveSpace) return null;

  // Blank spacer only — no border, label, or empty well chrome.
  if (!reference) {
    return (
      <div
        className={cn("flex min-w-0 flex-col gap-1", className)}
        aria-hidden
      >
        <span className="h-[14px]" />
        <div className={WELL_HEIGHT} />
        <div className="min-h-[16px]" />
      </div>
    );
  }

  // A pasted carousel pages through every slide instead of showing only its cover.
  const slides = reference.items ?? [];
  const canPlay = !!reference.videoUrl && slides.length <= 1;

  return (
    <div className={cn("flex min-w-0 flex-col gap-1", className)}>
      <span className="font-section text-[9.5px] font-semibold uppercase tracking-[0.14em] text-co-faint">
        Reference
      </span>
      <div
        className={cn(
          WELL_HEIGHT,
          "relative min-w-0 w-full overflow-hidden rounded-card bg-co-surface-solid",
        )}
      >
        {slides.length > 1 ? (
          <MediaCarousel items={slides} label={reference.label} />
        ) : playing && reference.videoUrl ? (
          <video
            src={reference.videoUrl}
            poster={reference.thumbnailUrl ?? undefined}
            controls
            autoPlay
            playsInline
            className="absolute inset-0 size-full max-h-full max-w-full bg-black object-contain"
          />
        ) : (
          <>
            {reference.thumbnailUrl ? (
              <Image
                src={reference.thumbnailUrl}
                alt={reference.label}
                loading="lazy"
                referrerPolicy="no-referrer"
                className="absolute inset-0 size-full max-h-full max-w-full object-contain"
              />
            ) : reference.videoUrl ? (
              // Harvest historically stored no thumbnail — first frame of the
              // mirrored video is the fallback until a real cover exists.
              <video
                src={reference.videoUrl}
                muted
                playsInline
                preload="metadata"
                className="absolute inset-0 size-full max-h-full max-w-full bg-black object-contain"
              />
            ) : (
              <div className="absolute inset-0 grid place-items-center bg-co-hover">
                <Film className="size-5 text-co-faint" strokeWidth={1.6} />
              </div>
            )}
            {canPlay && (
              <button
                type="button"
                onClick={() => setPlaying(true)}
                aria-label={`Play reference: ${reference.label}`}
                className="absolute inset-0 grid place-items-center bg-black/25 transition hover:bg-black/40"
              >
                <span className="grid size-9 place-items-center rounded-full bg-black/60 text-white">
                  <Play className="size-4 translate-x-[1px]" fill="currentColor" strokeWidth={0} />
                </span>
              </button>
            )}
          </>
        )}
      </div>
      <div className="flex min-h-[16px] items-center gap-1.5">
        <span
          className="min-w-0 flex-1 truncate text-[10.5px] text-co-faint"
          title={reference.label}
        >
          {reference.label}
        </span>
        {reference.pageUrl && (
          <a
            href={reference.pageUrl}
            target="_blank"
            rel="noreferrer"
            title={`Open on ${reference.platform}`}
            aria-label={`Open on ${reference.platform}`}
            className="grid size-6 shrink-0 place-items-center text-co-muted transition hover:text-co-text"
          >
            <PlatformOpenIcon platform={reference.platform} />
          </a>
        )}
      </div>
    </div>
  );
}

export default ReferenceWell;
